import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import { FrontendTaskStatus, Nullable, TaskData, TaskUrgency } from 'chaika-frontend-types/frontend-cms';

import { DateHelper } from '@utils/DateHelper';
import { TimezoneHelper } from '@utils/TimezoneHelper';

import type { PatientDataFieldNames } from '@store/Task';

import { CALL_TASK_TYPE_ID, TaskAssigneeType } from '../constants';
import type { TaskFormState } from '../types';

export function createInitialTaskForm(timezone: string): TaskFormState {
    const today = startOfCurrentDayInTimezone(timezone);
    const todayEnd = endOfCurrentDayInTimezone(timezone);

    return {
        isEditMode: true,
        taskId: undefined,
        authorId: undefined,
        requesterId: undefined,
        executorIds: [],
        assigneeType: TaskAssigneeType.Executors,
        practiceIds: [],
        actualExecutorId: undefined,
        start: today,
        end: todayEnd,
        timezone,
        createdAt: undefined,
        type: CALL_TASK_TYPE_ID,
        description: undefined,
        comment: undefined,
        rejectionComment: undefined,
        patientId: undefined,
        medicalCaseIds: [],
        entryId: undefined,
        urgency: TaskUrgency.Routine,
        status: FrontendTaskStatus.Draft,
    };
}

export function resetTaskForm(state: TaskFormState, timezone: string): TaskFormState {
    return { ...createInitialTaskForm(timezone), ...pickPermissions(state) };
}

export function seedTaskForm(state: TaskFormState, task: TaskData): TaskFormState {
    return {
        ...createInitialTaskForm(task.timezone),
        ...pickPermissions(state),
        isEditMode: false,
        taskId: task.id,
        authorId: task.authorId,
        requesterId: task.requesterId,
        executorIds: task.executorIds,
        assigneeType: isEmpty(task.practiceIds) ? TaskAssigneeType.Executors : TaskAssigneeType.Practice,
        practiceIds: task.practiceIds,
        actualExecutorId: task.actualExecutorId,
        start: task.start ? new Date(task.start) : startOfCurrentDayInTimezone(task.timezone),
        end: task.end ? new Date(task.end) : endOfCurrentDayInTimezone(task.timezone),
        timezone: task.timezone,
        createdAt: task.createdAt ? new Date(task.createdAt) : undefined,
        urgency: task.urgency,
        type: task.type ?? CALL_TASK_TYPE_ID,
        description: task.description,
        comment: task.comment,
        rejectionComment: task.rejectionComment,
        patientId: task.patientId,
        medicalCaseIds: task.medicalCaseIds ?? [],
        entryId: task.parentEntryId,
        status: task.status ?? FrontendTaskStatus.Draft,
    };
}

export function applyPatientDataField(
    state: TaskFormState,
    name: PatientDataFieldNames,
    value?: string,
): TaskFormState {
    const next = { ...state, [`${name}Id`]: value ?? undefined };

    if (name === 'patient' && value === undefined) {
        next.medicalCaseIds = [];
        next.entryId = undefined;
    }

    return next;
}

export function applyAssigneeType(
    state: TaskFormState,
    assigneeType: TaskAssigneeType,
    defaultPracticeId?: string,
): TaskFormState {
    if (assigneeType === state.assigneeType) {
        return state;
    }

    return assigneeType === TaskAssigneeType.Practice
        ? { ...state, assigneeType, executorIds: [], practiceIds: compact([defaultPracticeId]) }
        : { ...state, assigneeType, practiceIds: [] };
}

export function applyStartDate(state: TaskFormState, value: Nullable<Date>): TaskFormState {
    if (isNil(value)) {
        return state;
    }

    const start = startOfDayInTimezone(value, state.timezone);
    const end =
        state.end && DateHelper.isAfter(start, state.end) ? endOfDayInTimezone(value, state.timezone) : state.end;

    return { ...state, start, end };
}

export function applyEndDate(state: TaskFormState, value: Nullable<Date>): TaskFormState {
    if (isNil(value)) {
        return state;
    }

    const end = endOfDayInTimezone(value, state.timezone);

    return {
        ...state,
        end:
            state.start && DateHelper.isAfter(state.start, end)
                ? endOfDayInTimezone(
                      TimezoneHelper.toTimeZoneFromLocalTimezone(state.start, state.timezone),
                      state.timezone,
                  )
                : end,
    };
}

export function applyUrgency(state: TaskFormState, urgency: TaskUrgency): TaskFormState {
    if (urgency !== TaskUrgency.Urgent) {
        return { ...state, urgency };
    }

    return {
        ...state,
        urgency,
        start: startOfCurrentDayInTimezone(state.timezone),
        end: endOfCurrentDayInTimezone(state.timezone),
    };
}

export function startOfDayInTimezone(date: Date, timezone: string): Date {
    return TimezoneHelper.zonedTimeToLocalTimezone(DateHelper.getStartOfDay(date), timezone);
}

export function startOfCurrentDayInTimezone(timezone: string): Date {
    return startOfDayInTimezone(TimezoneHelper.toTimeZoneFromLocalTimezone(new Date(), timezone), timezone);
}

export function endOfDayInTimezone(date: Date, timezone: string): Date {
    return TimezoneHelper.zonedTimeToLocalTimezone(DateHelper.getEndOfDay(date), timezone);
}

export function endOfCurrentDayInTimezone(timezone: string): Date {
    return endOfDayInTimezone(TimezoneHelper.toTimeZoneFromLocalTimezone(new Date(), timezone), timezone);
}

function pickPermissions(
    state: TaskFormState,
): Pick<TaskFormState, 'authUserId' | 'authorsAssistantId' | 'requestersAssistantId' | 'executorsAssistantId'> {
    return {
        authUserId: state.authUserId,
        authorsAssistantId: state.authorsAssistantId,
        requestersAssistantId: state.requestersAssistantId,
        executorsAssistantId: state.executorsAssistantId,
    };
}
